import { motion } from "framer-motion";
import {
  Check,
  Forward,
  MessageCircleMore,
  SendHorizontal,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import socket from "../../config/socketIoConfig";
import BaseUrl from "../../constants/BaseUrl";
import * as DataAction from "../../store/actions/data/DataAction";
import { ShowError, ShowSuccess } from "../../utils/ToasFunction";
import CompletePopup from "../popup/complete";

const Inbox = () => {
  const [chatDetails, setChatDetails] = useState();
  const {
    allActiveChatSessions,
    selectedSessionId,
    selectedCompletedSessionId,
    allCompletedChatSessions,
  } = useSelector((state) => state.data);
  const dispatch = useDispatch();

  const { currentShift } = useSelector((state) => state.data);

  const [isComplete, setIsComplete] = useState(false);
  const [transfer, setTranfer] = useState(false);

  const { loginSuccess } = useSelector((state) => state.auth);

  const [chatMessage, setChatMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Sync chatDetails with Redux state
  useEffect(() => {
    if (selectedSessionId && allActiveChatSessions?.length) {
      const currentSession = allActiveChatSessions.find(
        (session) => session._id === selectedSessionId
      );
      if (currentSession) {
        setChatDetails(currentSession);
      }
    } else if (selectedCompletedSessionId && allCompletedChatSessions?.length) {
      const currentSession = allCompletedChatSessions.find(
        (session) => session._id === selectedCompletedSessionId
      );
      if (currentSession) {
        setChatDetails(currentSession);
      }
    }
  }, [
    selectedSessionId,
    allActiveChatSessions,
    selectedCompletedSessionId,
    allCompletedChatSessions,
  ]);

  // Socket event handling for chat completion
  useEffect(() => {
    const handleChatReceive = (messageObj) => {
      // If we receive an empty array, it means a chat   was completed
      // and we need to refresh the active chat sessions
      if (Array.isArray(messageObj) && messageObj.length === 0) {
        // Clear the current selection immediately since the chat was completed
        if (selectedSessionId) {
          dispatch(DataAction.selectedSessionIdAction(null));
          setChatDetails(null);
        }

        // Refresh active chat sessions based on user type
        if (loginSuccess?.user?.type === "community") {
          dispatch(DataAction.GetAllActiveChatSessions(currentShift?._id));
        } else {
          dispatch(
            DataAction.GetAllActiveChatSessionsTechnical(currentShift?._id)
          );
        }
      }
    };

    // Set up socket listener
    socket.on("chat:receive", handleChatReceive);

    // Cleanup socket listener
    return () => {
      socket.off("chat:receive", handleChatReceive);
    };
  }, [loginSuccess, currentShift, selectedSessionId, dispatch]);

  const handleShowPopup = () => {
    setIsComplete(true);
  };

  const ChatUpdateHandler = () => {
    if (chatMessage === "") {
      ShowError("Please type a message first");
      return;
    }

    if (isSending) {
      return; // Prevent multiple sends
    }

    setIsSending(true);

    const messageObj = {
      id: loginSuccess?.user?._id,
      message: chatMessage,
      fullName: `${loginSuccess?.user?.firstName} ${loginSuccess?.user?.lastName}`,
      dateTime: new Date(),
      support: true,
    };

    dispatch(DataAction.addMessageToSession(selectedSessionId, messageObj));

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

    const raw = JSON.stringify({ chatObje: messageObj });

    const requestOptions = {
      method: "PUT",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    fetch(
      `${BaseUrl}api/support/updateChatSession/${selectedSessionId}`,
      requestOptions
    )
      .then((response) => response.json())
      .then((result) => {
        if (result.status === "Success") {
          ShowSuccess("Conversation updated");
          setChatMessage("");

          dispatch(DataAction.updateChatSession(result.data));

          // Emit socket event after DB update
          const receiverId = result?.data?.customerData?._id || null;
          if (receiverId) {
            socket.emit("chat:new", {
              to: receiverId,
              messageObj,
            });
          }
        } else {
          ShowError("Conversation is not updated");
          // Optionally revert the optimistic update here
        }
      })
      .catch((error) => {
        console.error(error);
        ShowError("Failed to send message");
        // Optionally revert the optimistic update here
      })
      .finally(() => {
        setIsSending(false); // Always reset sending state
      });
  };

  const messagesEndRef = useRef(null); // Reference for last message

  // Scroll to bottom on new message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatDetails?.conversation]);

  const [comLoader, setComLoader] = useState(false);

  const MarkChatComplted = () => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);
    setComLoader(true);
    const requestOptions = {
      method: "PUT",
      redirect: "follow",
      headers: myHeaders,
      body: JSON.stringify(chatDetails),
    };

    fetch(
      `${BaseUrl}api/support/updateChatSessionStatus/${selectedSessionId}/${currentShift?._id}`,
      requestOptions
    )
      .then((response) => response.json())
      .then((result) => {
        if (result.status === "Success") {
          setComLoader(false);
          setIsComplete(false);
          if (loginSuccess.user.type === "community") {
            dispatch(DataAction.GetAllActiveChatSessions(currentShift?._id));
          } else {
            dispatch(
              DataAction.GetAllActiveChatSessionsTechnical(currentShift?._id)
            );
          }
          ShowSuccess("Shift completed successfully");
        } else {
          setComLoader(false);
          ShowError("Oops! something went wrong");
        }
      })
      .catch((error) => console.error(error));
  };

  const TranferChatToTechnicaltStaff = () => {
    setComLoader(true);

    const date = new Date();
    const formattedDate = date.toISOString().split("T")[0];

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

    const raw = JSON.stringify({
      createDate: formattedDate,
      chatSessionId: selectedSessionId,
    });

    const requestOptions = {
      method: "PUT",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    fetch(`${BaseUrl}api/support/transferChatToTechnical`, requestOptions)
      .then((response) => response.json())
      .then((result) => {
        if (result.status === "Success") {
          setComLoader(false);
          setTranfer(false);
          ShowSuccess("Chat successfully transferred to the technical staff.");
        } else {
          setComLoader(false);
          ShowError(result.message);
        }
      })
      .catch((error) => console.error(error));
  };

  return (
    <div className="h-full w-full">
      <div className="h-full bg-[#0381CA]/5 rounded-2xl">
        <div className="flex flex-col h-full w-[97%] mx-auto">
          {/* Show message if no chat is selected */}
          {!selectedSessionId && !selectedCompletedSessionId ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircleMore color="#FF814E" size={70} />
                <p className="text-gray-500 mt-2">
                  Select a chat to start messaging
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Chat Messages */}
              <div className="flex-1 h-[88%] overflow-y-auto space-y-4 panelScroll">
                {chatDetails?.conversation?.map((msg, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }} // Smooth staggered animation
                  >
                    {msg.support ? (
                      <div className="flex items-start justify-end space-x-2">
                        <div className="bg-themeColor text-white rounded-l-2xl rounded-br-2xl p-3 shadow-sm max-w-xs">
                          <p className="text-sm font-popinsRegular">
                            {msg.message}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start space-x-1 pt-3">
                        <img
                          src={
                            chatDetails?.customerData?.profileImage?.url ||
                            "/assets/daashboard/profile-complete/user-icon.png"
                          }
                          className="size-[45px] rounded-full"
                          alt="logo"
                        />
                        <div className="bg-white/70 p-3 rounded-r-2xl rounded-bl-2xl mt-3 shadow-sm max-w-[30rem]">
                          <p className="text-sm font-popinsRegular text-darkColor">
                            {msg.message}
                          </p>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )) || []}
                {(!chatDetails?.conversation ||
                  chatDetails?.conversation.length === 0) && (
                  <div className="w-full flex justify-center items-center h-full">
                    <div className="text-center">
                      <MessageCircleMore color="#FF814E" size={70} />
                      <p className="text-gray-500 mt-2">No messages yet</p>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} /> {/* Scroll to this element */}
              </div>

              {/* Input Area */}
              {selectedSessionId && (
                <div className="h-[12%] flex items-center">
                  <div className="w-[100%] flex justify-betwee bg-gray-100 rounded-full pr-2 justify-center items-center">
                    <input
                      value={chatMessage}
                      onChange={(e) => setChatMessage(e.target.value)}
                      type="text"
                      placeholder="Type a message..."
                      className="w-full px-5 py-3 font-popinsRegular text-sm rounded-full bg-gray-100"
                      disabled={isSending}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && !isSending) {
                          ChatUpdateHandler();
                        }
                      }}
                    />
                    <motion.div
                      whileTap={{ scale: 0.9 }}
                      whileHover={{ scale: isSending ? 1 : 1.2 }}
                      onClick={!isSending ? ChatUpdateHandler : undefined}
                      className={`cursor-pointer ml-2 size-8 rounded-full flex items-center justify-center ${
                        isSending ? "bg-gray-400" : "bg-themeColor"
                      }`}
                    >
                      <SendHorizontal size={20} color="#ffff" />
                    </motion.div>
                  </div>
                  <div className="w-[15%] flex justify-center items-center">
                    <motion.div
                      className="cursor-pointer"
                      whileHover={{ scale: 1.2 }}
                    >
                      {/* <Paperclip color="#1A1A1A" size={24} /> */}
                    </motion.div>
                    {loginSuccess?.user?.type === "community" && (
                      <>
                        {!chatDetails?.technicalStaffId && (
                          <motion.div
                            whileTap={{ scale: 0.9 }}
                            whileHover={{ scale: 1.2 }}
                            className="bg-black cursor-pointer ml-2 size-10 rounded-full flex items-center justify-center"
                            onClick={() => {
                              setTranfer(true);
                            }}
                          >
                            <Forward size={20} color="#ffff" />
                          </motion.div>
                        )}

                        <motion.div
                          whileTap={{ scale: 0.9 }}
                          whileHover={{ scale: 1.2 }}
                          className="bg-green-800 cursor-pointer ml-2 size-10 rounded-full flex items-center justify-center"
                          onClick={handleShowPopup}
                        >
                          <Check size={20} color="#ffff" />
                        </motion.div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {isComplete && (
        <CompletePopup
          loading={comLoader}
          msg={"Are you sure you want to complete this?"}
          onDeleteItem={MarkChatComplted}
          onClose={() => {
            setIsComplete(false);
          }}
        />
      )}

      {transfer && (
        <CompletePopup
          loading={comLoader}
          msg={"Are you sure you want to add technical staff to this chat?"}
          onDeleteItem={TranferChatToTechnicaltStaff}
          onClose={() => {
            setTranfer(false);
          }}
        />
      )}
    </div>
  );
};

export default Inbox;
